import { combineReducers } from 'redux'

import { uniqBy } from 'lodash'

import { initialBasket as basket } from '../../models/Basket';
import {
    ICollabSyncUi,
    IOrderState,
    State,
    OrderActions,
} from './orderStore';
import { ORDER_REVISION_POLL_INTERVAL_SECONDS } from '../../config/backgroundSyncConfig';

const defaultCollabSyncUi: ICollabSyncUi = {
    status: 'hidden',
    secondsRemaining: ORDER_REVISION_POLL_INTERVAL_SECONDS,
    pollIntervalSeconds: ORDER_REVISION_POLL_INTERVAL_SECONDS,
    nextCheckAt: null,
    checkNonce: 0,
    lastUpdatedAt: null,
    writeResetNonce: 0,
};

const order = (state: IOrderState = {
    isFetching: false,
    orders: [],
    closedOrders: [],
    basket,
    isDashboardFetching: false,
    dashboardDeliveryWindows: [],
    recentClosedOrders: [],
    orderIsPending: false,
    orderNotFound: false,
    allClosedOrders: [],
    allOpenOrders: [],
    allClosedOrdersPending: false,
    allOpenOrdersPending: false,
    remoteOrderSyncToken: 0,
    collabSyncUi: defaultCollabSyncUi,
    endCollabNonce: 0,
    collabActiveNonce: 0,
    activityFabVisibleNonce: 0,
    leaveCollabOrderNonce: 0,
    orderRevisionConflictVisible: false,
    orderActivity: {
        entries: [],
        isLoading: false,
        highlightOrderLineId: null,
    },
    orderRemotelyUnavailableReason: null,
    remoteOrderUnavailableNonce: 0,
}, action: OrderActions): IOrderState => {
    switch (action.type) {
        case 'GET_ORDER_SUCCESS':
        case 'SET_ORDER_SUCCESS':
            return { ...state, order: action.order }

        case 'GET_BASKET_SUCCESS':
            return { ...state, basket: action.basket }

        case 'GET_OPEN_ORDERS_SUCCESS':
            return { ...state, orders: action.orders }
        
        case 'APPLY_BASKET_CHANGES_SUCCESS':
        case 'RESET_BASKET_CHANGES_SUCCESS':
            const basketChanges = {...state.basket.changes, ...action.changes};
            const newBasket = state.basket;
            newBasket.changes = basketChanges;
            return { ...state, basket: newBasket}

        case 'ORDER_COMPLETED_SUCCESS':
            return { ...state }

        case 'ORDER_IS_PENDING':
            return { ...state, orderIsPending: action.payload }

        case 'GET_SHOPS_SUCCESS':
            return { ...state, shops: action.shops }

        case 'PEAK_NAV_ORDER_SUCCESS':
            return { ...state, navOrders: action.navOrders }

        case 'NAV_ORDER_CLOSED_SUCCESS':
            return { ...state, allClosedOrders: action.orders }
        
        case 'NAV_ORDER_OPEN_SUCCESS':
            return { ...state, allOpenOrders: action.orders }
        
        case 'GET_CUSTOMER_SHIPPING_ADDRESSES_SUCCESS':
            return { ...state, shippingAddresses: action.shippingAddresses }

        case 'SET_FETCHING_SHIPPING':
            return { ...state, isfetchingShippingAdresses: action.isFetching }
        
        case 'SET_FETCHING_ORDER':
            return { ...state, isFetching: action.isFetching }
        
        case 'SET_ORDER_NOT_FOUND':
            return { ...state, orderNotFound: action.payload }

        case 'GET_CLOSED_ORDERS_SUCCESS':
            return { ...state, closedOrders: action.closedOrders }
        
        case 'REMOVE_CLOSED_ORDER_TO_RECENT':
            return { ...state, recentClosedOrders: action.closedOrders }

        case 'DASHBOARD_IS_FETCHING':
            return { ...state, isDashboardFetching: action.isDashboardFetching }
        
        case 'GET_DASHBOARD_DATA_SUCCESS':
            let recentClosedOrdersLocalStorage = localStorage.getItem('recentClosedOrders')
            let localStorageClosedOrders: any[] = [];

            if (recentClosedOrdersLocalStorage) {
                localStorageClosedOrders = JSON.parse(recentClosedOrdersLocalStorage);
            }

            let recentClosedOrders = uniqBy([...state.recentClosedOrders, ...localStorageClosedOrders], 'appOrderId')

            return { ...state,
                dashboardOrder: action.dashboardData.dashboardOrder,
                dashboardDeliveryWindows: [],
                recentClosedOrders
            }

        case 'SET_DASHBOARD_DELIVERY_WINDOWS_SUCCESS':
            return { ...state, dashboardDeliveryWindows: action.dashboardDeliveryWindows}

        case 'CLEAR_ACTIVE_ORDER':
            delete state.order;
            delete state.basket;
            delete state.dashboardOrder;
            return {
                ...state,
                collabSyncUi: defaultCollabSyncUi,
                orderNotFound: false,
                orderRemotelyUnavailableReason: null,
                orderActivity: {
                    entries: [],
                    isLoading: false,
                    highlightOrderLineId: null,
                },
            }

        case 'ADD_CLOSED_ORDER_TO_RECENT':
            recentClosedOrdersLocalStorage = localStorage.getItem('recentClosedOrders')
            localStorageClosedOrders = [];

            if (recentClosedOrdersLocalStorage) {
                localStorageClosedOrders = JSON.parse(recentClosedOrdersLocalStorage);
            }

            recentClosedOrders = uniqBy([...state.recentClosedOrders, ...[action.order], ...localStorageClosedOrders], 'appOrderId')
            return { ...state, recentClosedOrders  }
        
        case 'CLEAR_DASHBOARD':
            delete state.dashboardOrder;
            return { ...state }
        
        case 'ORDER_USER_IS_INFORMED':
            return { ...state, userIsInformed: action.payload }

        case 'ORDER_REMOTE_SYNC':
            return { ...state, remoteOrderSyncToken: state.remoteOrderSyncToken + 1 }

        case 'SET_COLLAB_SYNC_UI':
            return { ...state, collabSyncUi: action.payload }

        case 'REQUEST_COLLAB_SYNC_CHECK': {
            const collabSyncUi = state.collabSyncUi || defaultCollabSyncUi;
            if (collabSyncUi.status === 'hidden' || collabSyncUi.status === 'syncing') {
                return state;
            }
            return {
                ...state,
                collabSyncUi: {
                    ...collabSyncUi,
                    checkNonce: collabSyncUi.checkNonce + 1,
                },
            };
        }

        case 'END_COLLAB_MODE':
            return { ...state, endCollabNonce: state.endCollabNonce + 1 }

        case 'COLLAB_ACTIVE_STATE_CHANGED':
            return { ...state, collabActiveNonce: state.collabActiveNonce + 1 }
        case 'ACTIVITY_FAB_VISIBILITY_CHANGED':
            return { ...state, activityFabVisibleNonce: state.activityFabVisibleNonce + 1 }

        case 'LEAVE_ORDER_FROM_COLLAB':
            return { ...state, leaveCollabOrderNonce: state.leaveCollabOrderNonce + 1 }

        case 'SHOW_ORDER_REVISION_CONFLICT':
            return { ...state, orderRevisionConflictVisible: true }

        case 'HIDE_ORDER_REVISION_CONFLICT':
            return { ...state, orderRevisionConflictVisible: false }

        case 'SET_ORDER_ACTIVITY_ENTRIES':
            return {
                ...state,
                orderActivity: {
                    ...state.orderActivity,
                    entries: action.payload,
                    isLoading: false,
                },
            }

        case 'APPEND_ORDER_ACTIVITY_ENTRIES': {
            const existingIds = new Set(state.orderActivity.entries.map((entry) => entry.id));
            const merged = [
                ...action.payload.filter((entry) => !existingIds.has(entry.id)),
                ...state.orderActivity.entries,
            ].sort((a, b) => b.revisionNumber - a.revisionNumber || b.id - a.id);

            return {
                ...state,
                orderActivity: {
                    ...state.orderActivity,
                    entries: merged,
                    isLoading: false,
                },
            };
        }

        case 'SET_ORDER_ACTIVITY_LOADING':
            return {
                ...state,
                orderActivity: {
                    ...state.orderActivity,
                    isLoading: action.payload,
                },
            }

        case 'SET_HIGHLIGHT_ORDER_LINE':
            return {
                ...state,
                orderActivity: {
                    ...state.orderActivity,
                    highlightOrderLineId: action.payload,
                },
            }

        case 'CLEAR_ORDER_ACTIVITY':
            return {
                ...state,
                orderActivity: {
                    entries: [],
                    isLoading: false,
                    highlightOrderLineId: null,
                },
            }

        case 'REMOTE_ORDER_UNAVAILABLE':
            return {
                ...state,
                orderRemotelyUnavailableReason: action.payload,
                remoteOrderUnavailableNonce: state.remoteOrderUnavailableNonce + 1,
                orderNotFound: action.payload === 'deleted' || action.payload === 'unavailable'
                    ? true
                    : state.orderNotFound,
            }
    }
    
    return state
}

export default combineReducers<State>({
    order
})